/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {Fragment, memo, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useForm} from 'react-hook-form'
import {FormattedMessage, useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'

import PropTypes from 'prop-types'

/* -----------------Adyen End ------------------------ */
import {Divider, Stack, Text} from '@chakra-ui/react'
import {
    AuthHelpers,
    useAuthHelper,
    useShopperBasketsMutation,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import Link from '@salesforce/retail-react-app/app/components/link'
import {usePromoCode} from '@salesforce/retail-react-app/app/components/promo-code'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getCreditCardIcon} from '@salesforce/retail-react-app/app/utils/cc-utils'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import {nanoid} from 'nanoid'

import {useTranslationHelpers} from '@epmc-mach/accelarator/overrides/app/hooks'

import {useCheckCustomerByEmail} from '../../../../hooks/use-check-customer-by-email'
import {isDuplicateAddress} from '../../checkout.helpers'
import {useCheckout} from '../../utils/checkout-context'
import subscribeNewsletterHelper from '../../utils/subscribe-newsletter'
import BillingAddressSelection from '../addresses/billing'
import {useCheckoutAddresses} from '../checkout-addresses-context'
import ErrorMessage from '../errorMessage'
import GuestAccountCreation from './guest-account-creation'
import MembershipSubscription from './membership-subscription'
/* -----------------Adyen Begin ------------------------ */
import AdyenCheckout from '/overrides/app/adyen/components/adyenCheckout'
import AdyenCheckoutProvider from '/overrides/app/adyen/contexts/adyen-checkout-context'
import CustomCheckbox from '/overrides/app/components/custom-checkbox'
import {ToggleCard, ToggleCardEdit} from '/overrides/app/components/toggle-card'
import {useNewsletterPreference} from '@epmc-mach/accelarator/overrides/app/hooks/use-newsletter-preference'

const normalizeAddress = (address) =>
    Object.entries({
        address1: address?.address1,
        address2: address?.address2,
        c_address3: address?.c_address3,
        companyName: address?.companyName,
        city: address?.city,
        countryCode: address?.countryCode?.toUpperCase(),
        firstName: address?.firstName,
        lastName: address?.lastName,
        phone: address?.phone,
        postalCode: address?.postalCode,
        stateCode: address?.stateCode
    })
        .filter(([, value]) => value !== undefined)
        .reduce((obj, [key, value]) => {
            obj[key] = value
            return obj
        }, {})

const Payment = () => {
    const contextData = useCheckoutAddresses()
    const [membershipPreferences, setMembershipPreferences] = useState({
        men: false,
        women: false
    })

    const [isEditingBillingAddress, setIsEditingBillingAddress] = useState(false)
    const [noBillingSelected, setNoBillingSelected] = useState(true)
    const {isNewsletterPreChecked, isLoading} = useNewsletterPreference()

    const membershipPreferencesRef = useRef(membershipPreferences)
    const {getTokenWhenReady} = useAccessToken()

    const {formatMessage} = useIntl()
    const {tCheckoutPage, tFormLabels, tShared} = useTranslationHelpers()
    const {site, locale} = useMultiSite()
    const {search} = useLocation()
    const query = new URLSearchParams(search)

    // Get all data from Context
    const {
        selectedShippingAddress,
        selectedBillingAddress,
        billingSameAsShipping,
        selectBillingAddress,
        toggleBillingSameAsShipping,
        syncAfterSave,
        basket,
        customer
    } = contextData

    const {step, STEPS, goToStep} = useCheckout()
    const billingSameAsShippingRef = useRef()
    const selectedBillingAddressRef = useRef()
    const isEdittingBillingAddressRef = useRef()

    // Computed billing address for display
    const displayBillingAddress = useMemo(() => {
        // don't show billingAddress & Payments if we are editing contact-info or Shipping Address
        if (step === STEPS.CONTACT_INFO || step === STEPS.SHIPPING_ADDRESS) return []
        if (billingSameAsShippingRef.current) {
            return selectedShippingAddress
        }
        // If no billing address selected, use first customer address as default
        if (!selectedBillingAddress && customer?.addresses?.length > 0) {
            const preferred = customer.addresses.find((addr) => addr.preferred)
            return preferred || customer.addresses[0]
        }
        return selectedBillingAddress || selectedShippingAddress
    }, [
        billingSameAsShipping,
        selectedBillingAddress,
        selectedShippingAddress,
        step,
        customer?.addresses
    ])

    const {mutateAsync: updateBillingAddressForBasket} = useShopperBasketsMutation(
        'updateBillingAddressForBasket'
    )
    const createCustomerAddress = useShopperCustomersMutation('createCustomerAddress')
    const updateCustomerAddress = useShopperCustomersMutation('updateCustomerAddress')

    const [isSubmittingPayment, setIsSubmittingPayment] = useState(false)
    const register = useAuthHelper(AuthHelpers.Register)
    const registerForm = useForm()

    // Form for billing address when editing
    const billingAddressForm = useForm({
        mode: 'onChange',
        shouldUnregister: false
    })

    // Using destructuring to remove properties from the object...
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {removePromoCode, ...promoCodeProps} = usePromoCode()

    const saveBillingAddress = async (address, shouldSaveToCustomer = true) => {
        if (!address || !basket?.basketId) return

        const workingAddress = normalizeAddress(address)

        try {
            // 1. Save to basket
            const {
                id: billingAddressId,
                fullName: billingAddressFullName,
                ...restCurrentBillingAddress
            } = basket?.billingAddress || {}
            if (!isEqual(workingAddress, restCurrentBillingAddress)) {
                await updateBillingAddressForBasket({
                    parameters: {basketId: basket.basketId},
                    body: workingAddress
                })
            }

            // 2. Save to customer addresses if registered and it's a new address
            if (customer.isRegistered && shouldSaveToCustomer) {
                const addressId = address.addressId
                if (!addressId) {
                    const existingAddresses = customer?.addresses || []
                    const duplicateFound = isDuplicateAddress(workingAddress, existingAddresses)
                    if (!duplicateFound) {
                        await createCustomerAddress.mutateAsync({
                            body: {
                                ...workingAddress,
                                addressId: `${workingAddress?.city.replace(/\s+/g, '-')}-${nanoid(
                                    5
                                )}`
                            },
                            parameters: {customerId: customer.customerId}
                        })
                    }
                } else {
                    await updateCustomerAddress.mutateAsync({
                        body: {
                            ...address,
                            countryCode: workingAddress?.countryCode
                        },
                        parameters: {
                            customerId: customer.customerId,
                            addressName: addressId
                        }
                    })
                }
            }

            // 4. Update UI and sync after save
            selectBillingAddress(address)
            await syncAfterSave()
        } catch (error) {
            console.error('❌ Error saving billing address:', error)
            throw error // Re-throw to handle in calling function
        }
    }

    const handleSameAsShipping = (e) => {
        const isChecked = e.target.checked
        toggleBillingSameAsShipping(isChecked)

        if (!isChecked) {
            setIsEditingBillingAddress(false)
        }
    }

    const handleAddressSelect = (address) => {
        setNoBillingSelected(false)
        billingAddressForm.reset()
        selectBillingAddress(address)
    }

    const validateBillingAddressForm = async () => {
        const isValid = await billingAddressForm.trigger()

        if (!isValid) {
            setIsSubmittingPayment(false)
            throw new Error('Billing error')
        }

        return isValid
    }

    const getBillingAddressFromForm = () => {
        const formValues = billingAddressForm.getValues()
        const transformedAddress = {
            addressId: formValues.addressId || ''
        }

        Object.keys(formValues).forEach((key) => {
            if (key.startsWith('billing_')) {
                transformedAddress[key.replace('billing_', '')] = formValues[key]
            }
        })

        return transformedAddress
    }

    const handleSaveSelectedAddress = async () => {
        setIsSubmittingPayment(true)
        try {
            if (billingSameAsShippingRef.current) {
                await saveBillingAddress(selectedShippingAddress, false)
            } else {
                // Check if we're editing/creating an address
                if (isEdittingBillingAddressRef.current || customer?.isGuest) {
                    await validateBillingAddressForm()

                    const transformedAddress = getBillingAddressFromForm()

                    await saveBillingAddress(transformedAddress, true)
                    selectBillingAddress(transformedAddress)
                    setIsEditingBillingAddress(false)
                } else if (displayBillingAddress) {
                    await saveBillingAddress(selectedBillingAddressRef.current, true)
                } else {
                    console.error('❌ No billing address selected or entered')
                }
            }
        } catch (e) {
            throw new Error('Billing error')
        } finally {
            setIsSubmittingPayment(false)
        }
    }

    const onBillingSubmit = async (state, component, actions, props) => {
        // TODO: All this mess here is because we don't have time to rewrite the main logic since it will require time we don't have
        // This is not clean solution and workaround with some code duplication to prevent current issues
        // We need to address it when we have time

        let billingAddress = null

        if (customer?.isGuest && !billingSameAsShippingRef.current) {
            try {
                await validateBillingAddressForm()

                billingAddress = getBillingAddressFromForm()
            } catch {
                props?.setOrderNo('fakeid')
                // exit here because initially this check happens below
                // but we need to do this check because we need to save billing address after user is registered
                return
            }
        }

        const token = await getTokenWhenReady()
        const password = registerForm.watch('password')
        const email = basket.customerInfo?.email ?? customer?.email

        try {
            if (customer?.isGuest && password) {
                const newCustomer = await registerCustomer(billingAddress)
                if (!newCustomer) {
                    const {
                        formState: {errors}
                    } = registerForm
                    site.errors = errors
                }
            }
        } catch (error) {
            // We don't have error flow on account creation/syncing data to sfsc, one.
            // Since it's happening when we will submit payment
            // and payment has higher priority
        }

        const personalInfo = {
            email,
            firstName: selectedShippingAddress.firstName,
            lastName: selectedShippingAddress.lastName,
            membershipPreferences: membershipPreferencesRef.current
        }

        const tagName = tCheckoutPage?.tagName

        subscribeNewsletterHelper(personalInfo, site?.id, token, tagName, locale.id)

        try {
            await handleSaveSelectedAddress()
        } catch (e) {
            props?.setOrderNo('fakeid')
        }
    }

    const memoizedMembershipPreferences = useMemo(
        () => membershipPreferences,
        [membershipPreferences]
    )

    const handleMembershipPreferencesUpdate = useCallback((newPreferences) => {
        setMembershipPreferences(newPreferences)
    }, [])

    useEffect(() => {
        if (!isLoading) {
            setMembershipPreferences({
                men: isNewsletterPreChecked,
                women: isNewsletterPreChecked
            })
        }
    }, [isNewsletterPreChecked, isLoading])

    useEffect(() => {
        membershipPreferencesRef.current = memoizedMembershipPreferences
    }, [memoizedMembershipPreferences])

    useEffect(() => {
        billingSameAsShippingRef.current = billingSameAsShipping
    }, [billingSameAsShipping])

    useEffect(() => {
        selectedBillingAddressRef.current = selectedBillingAddress
    }, [selectedBillingAddress])

    useEffect(() => {
        isEdittingBillingAddressRef.current = isEditingBillingAddress
    }, [isEditingBillingAddress])

    // Set initial billing address when switching to separate billing
    useEffect(() => {
        if (!billingSameAsShippingRef.current && !selectedBillingAddress && displayBillingAddress) {
            selectBillingAddress(displayBillingAddress)
        }
    }, [billingSameAsShipping, selectedBillingAddress, displayBillingAddress])

    const shouldShowGuestAccountCreation = useCheckCustomerByEmail()

    const registerCustomer = useCallback(
        async (billingAddress) => {
            try {
                registerForm.clearErrors('global')
                const isPasswordValid = await registerForm.trigger()
                if (!isPasswordValid) {
                    return
                }

                const body = {
                    customer: {
                        firstName: basket?.shipments?.[0]?.shippingAddress?.firstName || '',
                        lastName: basket?.shipments?.[0]?.shippingAddress?.lastName || '',
                        email: basket?.customerInfo?.email || '',
                        login: basket?.customerInfo?.email || '',
                        c_newsletterSubscription: registerForm.watch('subscription'),
                        preferredLocale: locale.id.replace('-', '_')
                    },
                    password: registerForm.watch('password')
                }

                const newCustomer = await register.mutateAsync(body)

                const shippingAddress = basket?.shipments?.[0]?.shippingAddress

                if (shippingAddress) {
                    await createCustomerAddress.mutateAsync({
                        body: {
                            ...normalizeAddress(shippingAddress),
                            preferred: true,
                            addressId: `${shippingAddress?.city.replace(/\s+/g, '-')}-${nanoid(5)}`
                        },
                        parameters: {customerId: newCustomer.customerId}
                    })
                }

                if (billingAddress) {
                    await createCustomerAddress.mutateAsync({
                        body: {
                            ...normalizeAddress(billingAddress),
                            c_isBillingAddress: true,
                            addressId: `${billingAddress?.city.replace(/\s+/g, '-')}-${nanoid(5)}`
                        },
                        parameters: {customerId: newCustomer.customerId}
                    })
                }
            } catch (error) {
                if (!error.response) {
                    registerForm.setError('global', {type: 'manual', message: API_ERROR_MESSAGE})
                    return
                }
                const json = await error.response.json()

                const existingAccountMessage = (
                    <Fragment>
                        <FormattedMessage
                            defaultMessage="This email already has an account."
                            id="checkout_confirmation.message.already_has_account"
                        />{' '}
                        <Link to="/login" color="gray.600">
                            <FormattedMessage
                                defaultMessage="Log in here"
                                id="checkout_confirmation.link.login"
                            />
                        </Link>
                    </Fragment>
                )

                const message = /the login is already in use/i.test(json.detail)
                    ? existingAccountMessage
                    : formatMessage(API_ERROR_MESSAGE)

                registerForm.setError('global', {type: 'manual', message})
            }
        },
        [registerForm, basket, register, formatMessage, createCustomerAddress]
    )

    const checkIfBasketIsNotReady = () =>
        isEmpty(basket?.shipments[0]?.shippingAddress) || isEmpty(basket.customerInfo?.email)

    return (
        <ToggleCard
            id="step-3"
            title={tCheckoutPage?.step3Title}
            editing={step === STEPS.PAYMENT}
            isLoading={isSubmittingPayment}
            disabled={checkIfBasketIsNotReady()}
            isSelectedAddress={!isEmpty(displayBillingAddress)}
            onEdit={() => goToStep(STEPS.PAYMENT)}
            editLabel={step !== STEPS.PAYMENT && tShared?.globalEditButton}
            isPaymentStep={true}
        >
            <ToggleCardEdit>
                {!isEmpty(selectedShippingAddress) && (
                    <Stack spacing={6}>
                        <Stack spacing={2}>
                            <CustomCheckbox
                                name="billingSameAsShipping"
                                checked={billingSameAsShipping}
                                onChange={handleSameAsShipping}
                                label={tFormLabels?.checkboxBillingAddress}
                            />
                        </Stack>

                        {!billingSameAsShipping && (
                            <BillingAddressSelection
                                selectedAddress={displayBillingAddress}
                                billingForm={billingAddressForm}
                                onAddressSelect={handleAddressSelect}
                                setIsEditingAddress={setIsEditingBillingAddress}
                                isEditingAddress={isEditingBillingAddress}
                                noBillingSelected={noBillingSelected}
                            />
                        )}

                        {shouldShowGuestAccountCreation && (
                            <Stack variant="form">
                                <GuestAccountCreation
                                    registerForm={registerForm}
                                    onRegisterCustomer={registerCustomer}
                                />
                            </Stack>
                        )}

                        <MembershipSubscription
                            basket={basket}
                            customer={customer}
                            membershipPreferences={memoizedMembershipPreferences}
                            setMembershipPreferences={handleMembershipPreferencesUpdate}
                        />

                        <Divider borderColor="gray.100" />

                        {query.get('error') && <ErrorMessage />}

                        {step === STEPS.PAYMENT && (
                            <AdyenCheckoutProvider>
                                <AdyenCheckout beforeSubmit={[onBillingSubmit]} />
                            </AdyenCheckoutProvider>
                        )}
                    </Stack>
                )}
            </ToggleCardEdit>
        </ToggleCard>
    )
}

const PaymentCardSummary = ({payment}) => {
    if (!payment?.paymentCard) {
        return null
    }
    const CardIcon = getCreditCardIcon(payment?.paymentCard?.cardType)
    return (
        <Stack direction="row" alignItems="center" spacing={3}>
            {CardIcon && <CardIcon layerStyle="ccIcon" />}

            <Stack direction="row">
                <Text>{payment.paymentCard.cardType}</Text>
                <Text>&bull;&bull;&bull;&bull; {payment.paymentCard.numberLastDigits}</Text>
                <Text>
                    {payment.paymentCard.expirationMonth}/{payment.paymentCard.expirationYear}
                </Text>
            </Stack>
        </Stack>
    )
}

PaymentCardSummary.propTypes = {payment: PropTypes.object}

Payment.propTypes = {}

export default memo(Payment)
